// src/services/series.js
// Data layer for SERIES — free, self-paced "challenge" collections that appear
// as the 4th Workouts tab. See docs/decisions/adr-series.md + docs/ux/series-tab.md.
//
// Series live in their OWN Firestore collection (`series`), deliberately
// isolated from the commerce-wired `programs` collection (Stripe / pods /
// purchasedPrograms). They reuse the Bunny collection + videoOrder[] pattern
// from programs, but share NONE of the program read/write paths.
//
// Doc schema:
//   {
//     title, description,            // description is FormattedText markdown
//     imageUrl,                      // cover; UI falls back to branded splash
//     bunnyCollectionId,             // source of the day videos
//     videoOrder: string[],          // ordered Bunny videoIds → days 1..N
//     dayLabels: { [videoId]: str }, // optional per-day override; blank ⇒ auto {video title}
//     active: boolean,               // published (non-admins see it only when true + flag on)
//     order: number,                 // list sort
//     createdAt,
//
//     // ── Rich content + deep link (2026-09-10). ALL OPTIONAL — a series with
//     // none of these set renders exactly as it did before they existed.
//     // See docs/decisions/adr-series-deep-link-and-sections.md.
//     slug,              // shareable link token: /workouts?series=<slug>; falls back to doc id
//     tagline,           // one quiet line under the title
//     metaPills: str[],  // free-text chips ("No equipment", "10 min a day")
//     highlightsHeading, // per-series override of siteConfig.seriesHighlightsHeading
//     highlights: str[], // one benefit per line
//     sections: [{ heading, body }],   // flexible, repeatable; body is FormattedText markdown
//     faqsHeading,       // per-series override of siteConfig.seriesFaqHeading
//     faqs: [{ question, answer }],    // same shape as programs.faqs
//     daysHeading,       // optional heading above the day list; absent ⇒ no heading (today's layout)
//     detailsFirst,      // bool: render the info blocks ABOVE the day list
//     releaseDates: { [videoId]: 'YYYY-MM-DD' },  // optional per-day scheduled
//                        // release; absent/blank => available immediately
//
//     publicAccess,      // bool (2026-09-14): when true AND active, the challenge's
//                        // link works for people with NO account. Optional and
//                        // absent on every pre-existing challenge => private.
//                        // See adr-public-series-access.md.
//
//     nonDayVideos: { [videoId]: kicker },  // (2026-09-14) videos in the list that
//                        // are NOT days -- a welcome note, a bonus, a cooldown.
//                        // KEY PRESENCE is the flag; the value is an optional
//                        // eyebrow label ('WELCOME'), '' for no eyebrow at all.
//                        // Excluded from the day count, the progress denominator
//                        // and the numbering, so the day after a welcome is Day 1.
//                        // Absent on every pre-existing challenge => all days.
//
//     hideVideoLengths: bool,  // (2026-09-19) opt out of the automatic duration
//                        // display for THIS challenge: the ' · N min' on each day
//                        // row, the 'N min total' hero chip, and the player's
//                        // duration pill -- in the app AND on the public link.
//                        // For challenges where the file length includes
//                        // intro/outro and the real workout time is stated in the
//                        // title/description. Absent/false => lengths show, which
//                        // is every pre-existing challenge.
//   }
//
// A series' days can MIX Bunny-collection videos with videos recorded in-app
// (MediaRecorder -> Mux). Recorded ones reuse the EXISTING `programVideos`
// collection with the seriesId stored in its `programId` field -- the same
// opaque-scope-token trick this feature already uses for completions. That means
// no new collection (so no new Firestore rule), and the Mux upload endpoint +
// webhook work unchanged. `videoOrder[]` interleaves Bunny videoIds and
// programVideos doc ids (distinct namespaces, so mixing is safe).
//
// Completions for a series day reuse the existing `programId` field as an opaque
// scope token (the seriesId is stored there) with source:'series' — so the
// completions core is untouched. See adr-series.md Decision 2.

import { db, firebaseEnabled } from './firebase';
import { fetchProgramVideos, deleteProgramVideo } from './programVideos';
import { trainerWallClockToUTC } from '../utils/timezone';
import {
    collection, doc, getDocs, query, orderBy, where, serverTimestamp,
} from 'firebase/firestore';
// Writes wait for the member's Firestore identity (adr-firestore-identity.md a2).
import {
    addDoc, updateDoc, deleteDoc,
} from './firestoreWrite';

// Fetch all series, sorted by `order` (ascending). Returns [] on empty,
// null when Firebase is disabled or the query fails — same contract as
// fetchPrograms so callers can treat null as "couldn't load".
export async function fetchSeries() {
    if (!firebaseEnabled) return null;
    try {
        const q = query(collection(db, 'series'), orderBy('order', 'asc'));
        const snap = await getDocs(q);
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) {
        // Missing `order` on legacy/partial docs would break orderBy — fall back
        // to an unordered read so the admin can still see + fix them.
        try {
            const snap = await getDocs(collection(db, 'series'));
            return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        } catch (e2) { console.error('fetchSeries failed:', e2?.message || e2); return null; }
    }
}

// Persist `data` wholesale (no field whitelist) — mirrors createProgram so new
// schema fields never need plumbing here.
export async function createSeries(data) {
    if (!firebaseEnabled) return null;
    try {
        const ref = await addDoc(collection(db, 'series'), { ...data, createdAt: serverTimestamp() });
        return ref.id;
    } catch (e) {
        // Surface the cause (e.g. "Missing or insufficient permissions" when the
        // `series` Firestore rule hasn't been added) — silent null here once cost
        // an hour of "it says Saved but nothing persists" debugging.
        console.error('createSeries failed:', e?.message || e);
        return null;
    }
}

export async function updateSeries(id, data) {
    if (!firebaseEnabled) return false;
    try { await updateDoc(doc(db, 'series', id), data); return true; }
    catch (e) { console.error('updateSeries failed:', e?.message || e); return false; }
}

// Delete a series + best-effort clean up its completions. Series completions
// store the seriesId in the `programId` field (adr-series.md Decision 2), so we
// clean by querying completions where programId == seriesId — mirrors the
// completion cleanup in deleteProgram.
export async function deleteSeries(id) {
    if (!firebaseEnabled) return false;
    try {
        try {
            const compQuery = query(collection(db, 'completions'), where('programId', '==', id));
            const compSnap = await getDocs(compQuery);
            if (compSnap.size > 0) {
                await Promise.all(compSnap.docs.map((d) => deleteDoc(doc(db, 'completions', d.id))));
                console.log(`Cleaned up ${compSnap.size} completion(s) for deleted series ${id}`);
            }
        } catch (cleanupErr) { console.warn('Non-critical: series completions cleanup failed:', cleanupErr); }
        // Recorded (in-app / Mux) day videos are owned by this series, so remove
        // them too -- otherwise they'd linger unreachable in `programVideos`.
        // Best-effort, exactly like the completions cleanup above: a failure here
        // must never block deleting the series.
        try {
            const recorded = await fetchProgramVideos(id);
            if (recorded && recorded.length > 0) {
                await Promise.all(recorded.map((pv) => deleteProgramVideo(pv.id)));
                console.log(`Cleaned up ${recorded.length} recorded video(s) for deleted series ${id}`);
            }
        } catch (recErr) { console.warn('Non-critical: series recorded-video cleanup failed:', recErr); }
        await deleteDoc(doc(db, 'series', id));
        return true;
    } catch (e) { console.error('Error deleting series:', e); return false; }
}

// Resolve the display label for a given day: explicit override wins, else the
// video's parsed title, else the generic "Day N" from the template. Pure helper
// (no I/O) so both the admin editor and the member view render identically.
export function seriesDayLabel({ video, dayLabels, dayNumber, template }) {
    const override = dayLabels && video && dayLabels[video.id];
    if (override && override.trim()) return override.trim();
    if (video && video.title && video.title.trim()) return video.title.trim();
    return (template || 'Day {n}').replace('{n}', String(dayNumber));
}

// Order a Bunny video list by a series' videoOrder[]. Videos present in the
// collection but missing from videoOrder are appended (so a newly-uploaded
// Bunny video still surfaces); ids in videoOrder with no matching video are
// dropped. Mirrors the program ordering behavior.
export function orderSeriesVideos(videos, videoOrder) {
    if (!Array.isArray(videos)) return [];
    if (!Array.isArray(videoOrder) || videoOrder.length === 0) return videos;
    const byId = new Map(videos.map((v) => [v.id, v]));
    const ordered = [];
    for (const id of videoOrder) {
        if (byId.has(id)) { ordered.push(byId.get(id)); byId.delete(id); }
    }
    // Append any videos not referenced in videoOrder, preserving their order.
    for (const v of videos) { if (byId.has(v.id)) ordered.push(v); }
    return ordered;
}

// ── Deep-link helpers (pure) ────────────────────────────────────────────────
// A challenge is addressable at /workouts?series=<slug-or-id>. The slug is
// optional: links work off the Firestore doc id from the moment a series
// exists, and adding a slug later never breaks an already-shared id link
// (findSeriesByToken matches either). See adr-series-deep-link-and-sections.md.

// Turn a title into a URL-safe slug suggestion. Admin-facing convenience only —
// the admin can edit or clear it.
export function slugifySeriesTitle(title) {
    return String(title || '')
        .toLowerCase()
        .normalize('NFKD').replace(/[\u0300-\u036f]/g, '') // strip accents
        .replace(/['’]/g, '')                              // don't hyphenate apostrophes
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 60);
}

// Resolve a ?series= token against a loaded list. Slug wins over id so a
// deliberate slug can never be shadowed by an unrelated doc id. Returns null
// when nothing matches (caller shows the calm not-found notice).
export function findSeriesByToken(list, token) {
    const t = String(token || '').trim().toLowerCase();
    if (!t || !Array.isArray(list)) return null;
    return (
        list.find((s) => s && s.slug && String(s.slug).trim().toLowerCase() === t) ||
        list.find((s) => s && s.id && String(s.id).toLowerCase() === t) ||
        null
    );
}

// The canonical absolute link for a series. Prefers the slug; falls back to the
// doc id. `origin` is injected so this stays pure + testable.
export function seriesShareUrl(series, origin) {
    if (!series) return '';
    const token = (series.slug && String(series.slug).trim()) || series.id;
    const base = origin || (typeof window !== 'undefined' ? window.location.origin : '');
    return `${base}/workouts?series=${encodeURIComponent(token)}`;
}

// True when another series in the list already uses this slug — drives the
// admin duplicate warning. (Uniqueness is advisory: findSeriesByToken picks the
// first match, so a collision degrades to "wrong challenge", never an error.)
export function isSlugTaken(list, slug, exceptId) {
    const s = String(slug || '').trim().toLowerCase();
    if (!s || !Array.isArray(list)) return false;
    return list.some((x) => x && x.id !== exceptId && String(x.slug || '').trim().toLowerCase() === s);
}


// ── Public access (no account required) ────────────────────────────────────
// TWO gates in series, and both must pass:
//   active       — publishing is what makes a challenge real
//   publicAccess — decides who may see something already published
// A draft is therefore NEVER public, whatever the toggle says. Collapsing these
// into one switch would let an unfinished challenge leak the moment someone
// flipped the wrong control.
//
// Fail closed: only a literal `true` opens the door, so every challenge that
// predates this field stays exactly as private as it is today.
// See docs/decisions/adr-public-series-access.md.
export function isSeriesPublic(series) {
    return !!series && series.publicAccess === true && series.active === true;
}


// ── Days vs. extras ────────────────────────────────────────────────────────
// Not everything in a challenge's list is a "day". A welcome note, a bonus or a
// cooldown sits in the running order but shouldn't carry a number, shouldn't
// inflate "3 of 7 days", and shouldn't push the first real workout to Day 2.
//
// `nonDayVideos` is a map whose KEY PRESENCE marks a video as an extra; the
// value is an optional eyebrow label. Presence (not truthiness) is the test, so
// an extra with no label — the common case — still counts as an extra.
export function isSeriesExtra(nonDayVideos, videoId) {
    return !!nonDayVideos && Object.prototype.hasOwnProperty.call(nonDayVideos, videoId);
}

// The optional eyebrow for an extra ('WELCOME'). Blank ⇒ the row shows no
// eyebrow line at all rather than an empty one.
export function seriesExtraKicker(nonDayVideos, videoId) {
    const v = nonDayVideos && nonDayVideos[videoId];
    return (typeof v === 'string' && v.trim()) ? v.trim() : '';
}

// Walk an ORDERED video list once and resolve each row's identity:
//   { video, isDay, dayNumber, kicker }
// Day numbers count only days, so extras never consume one. With no
// `nonDayVideos` this returns dayNumber === index + 1 for every row — i.e.
// exactly the positional numbering that preceded this feature.
export function indexSeriesDays(videos, nonDayVideos) {
    let n = 0;
    return (Array.isArray(videos) ? videos : []).map((video) => {
        const extra = isSeriesExtra(nonDayVideos, video.id);
        if (!extra) n += 1;
        return {
            video,
            isDay: !extra,
            dayNumber: extra ? null : n,
            kicker: extra ? seriesExtraKicker(nonDayVideos, video.id) : '',
        };
    });
}

// How many rows actually count as days — the denominator for "3 of 7 days".
export function seriesDayCount(videos, nonDayVideos) {
    return (Array.isArray(videos) ? videos : []).filter((v) => !isSeriesExtra(nonDayVideos, v.id)).length;
}


// ── Scheduled day release (optional drip) ──────────────────────────────────
// A day may carry a release DATE (YYYY-MM-DD). It unlocks at 1:00am Eastern on
// that date — which is midnight Central, i.e. the very start of that day on
// Danielle's clock. Those are the same instant year-round (both zones shift for
// DST together), so this reuses the app's DST-proven Central helper rather than
// introducing a second timezone.
//
// No release date ⇒ available immediately, which is every existing day.
// This is PACING, not access control: the videos are free and the gate is
// client-side, exactly like the cohort week drip.

// The moment a day unlocks, or null when it has no schedule.
export function seriesDayReleaseAt(releaseDates, videoId) {
    const raw = releaseDates && videoId ? releaseDates[videoId] : null;
    if (!raw || !String(raw).trim()) return null;
    return trainerWallClockToUTC(String(raw).trim(), '00:00');  // 00:00 CT == 1:00am ET
}

// Has this day unlocked yet? Unscheduled days are always true.
// `now` is injected so callers can pass the server-synced clock.
export function isSeriesDayReleased(releaseDates, videoId, now) {
    const at = seriesDayReleaseAt(releaseDates, videoId);
    if (!at) return true;
    const t = now instanceof Date ? now.getTime() : (typeof now === 'number' ? now : Date.now());
    return t >= at.getTime();
}

// "Mon, Sep 15" — rendered in the VIEWER's locale from the true unlock instant,
// so someone in another timezone sees the moment it opens for them.
export function formatSeriesReleaseDate(date) {
    if (!date) return '';
    try {
        return date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
    } catch (_) { return ''; }
}

// Bulk scheduler for the admin: day 1 on `startDate`, then every `everyNDays`.
// Pure — returns a fresh { [videoId]: 'YYYY-MM-DD' } map for the given order.
export function buildSeriesReleaseSchedule(orderedVideoIds, startDate, everyNDays) {
    const out = {};
    if (!Array.isArray(orderedVideoIds) || !startDate) return out;
    const [y, mo, d] = String(startDate).split('-').map(Number);
    if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return out;
    const step = Math.max(1, Number(everyNDays) || 1);
    orderedVideoIds.forEach((id, i) => {
        // UTC arithmetic purely as a calendar counter — no clock semantics here,
        // the CT/ET conversion happens later in seriesDayReleaseAt.
        const dt = new Date(Date.UTC(y, mo - 1, d + (i * step)));
        const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
        const dd = String(dt.getUTCDate()).padStart(2, '0');
        out[id] = `${dt.getUTCFullYear()}-${mm}-${dd}`;
    });
    return out;
}
